"use client";

import { motion } from "framer-motion";
import { PageHeader } from "@/components/shared/PageHeader";
import { EASE } from "@/lib/motion";

type Section = {
  heading: string;
  content: string;
  links?: { label: string; href: string }[];
};

type Props = {
  badge: string;
  title: string;
  subtitle: string;
  sections: Section[];
};

export default function LegalPageShell({ badge, title, subtitle, sections }: Props) {
  return (
    <main className="min-h-screen bg-paper">
      <PageHeader eyebrow={badge} title={title} subtitle={subtitle} />

      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Company info */}
          <div className="enter [--enter-y:16px] [--enter-delay:100ms] [animation-duration:0.5s] mb-14 p-6 rounded-2xl bg-paper-deep border border-line">
            <p className="font-display font-bold text-ink">futuredoctor GmbH</p>
            <p className="text-ink-soft text-sm mt-1">
              Bettinastraße 62, 60325 Frankfurt am Main &middot; info@ncrechner.de
            </p>
          </div>

          {/* Sections */}
          <div>
            {sections.map(({ heading, content, links }, i) => (
              <motion.div
                key={heading}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.04, ease: EASE }}
                className="grid sm:grid-cols-[3rem_1fr] gap-x-6 py-8 border-t border-line last:border-b"
              >
                <span className="font-display font-semibold text-accent tnum text-base leading-none pt-1 max-sm:mb-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-display font-bold text-lg text-ink tracking-tight">
                    {heading}
                  </h2>
                  <p className="text-ink-soft text-sm leading-relaxed whitespace-pre-line mt-3">
                    {content}
                  </p>
                  {links && links.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-4">
                      {links.map(({ label, href }) => (
                        <a
                          key={href}
                          href={href}
                          className="inline-flex items-center gap-1 text-accent hover:text-accent-deep text-sm font-medium underline underline-offset-4 decoration-accent/40 transition-colors cursor-pointer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bottom note */}
          <p className="mt-14 text-center text-ink-faint text-xs">
            © {new Date().getFullYear()} futuredoctor GmbH · Bettinastraße 62, 60325 Frankfurt am Main
          </p>
        </div>
      </section>
    </main>
  );
}
